import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';
import { CustomerStats, ProjectStats } from 'src/shared/types';

import {
    createCustomerMutation,
    CreateCustomerParams,
    CreateCustomerResult,
    customerStatsQuery,
    CustomerStatsParams,
    CustomerStatsResult,
    deleteCustomerMutation,
    DeleteCustomerParams,
    DeleteCustomerResult,
    updateCustomerMutation,
    UpdateCustomerParams,
    UpdateCustomerResult,
} from './queries/customerAdminQuery';
import {
    buildCreateProjectVariables,
    createProjectMutation,
    CreateProjectParams,
    CreateProjectResult,
    customerProjectDefinitionsQuery,
    CustomerProjectDefinitionsParams,
    CustomerProjectDefinitionsResult,
    deleteProjectMutation,
    DeleteProjectParams,
    DeleteProjectResult,
    ProjectDefinitionListRow,
    projectStatsQuery,
    ProjectStatsParams,
    ProjectStatsResult,
    setProjectPortfolioInclusionMutation,
    SetProjectPortfolioInclusionParams,
    SetProjectPortfolioInclusionResult,
    updateProjectMutation,
    UpdateProjectParams,
    UpdateProjectResult,
} from './queries/projectAdminQuery';

const adminGraphSlice = graphApiSlice.injectEndpoints({
    endpoints: builder => ({
        createCustomer: builder.mutation<CreateCustomerResult, CreateCustomerParams>({
            query: params => ({
                body: createCustomerMutation,
                variables: params,
            }),
            // Refreshes the organisations list (customers query) once a new org is created.
            invalidatesTags: ['customer'],
        }),

        updateCustomer: builder.mutation<UpdateCustomerResult, UpdateCustomerParams>({
            query: params => ({
                body: updateCustomerMutation,
                variables: params,
            }),
            // The org list and org page both read the name from the customers query.
            invalidatesTags: ['customer'],
        }),

        deleteCustomer: builder.mutation<DeleteCustomerResult, DeleteCustomerParams>({
            query: params => ({
                body: deleteCustomerMutation,
                variables: params,
            }),
            // Deliberately invalidates nothing: the caller does it once the operator has read the
            // receipt, because re-reading the lists unmounts the dialog showing it. See
            // DeleteOrganisationDialog.
        }),

        createProject: builder.mutation<CreateProjectResult, CreateProjectParams>({
            query: params => ({
                body: createProjectMutation,
                variables: buildCreateProjectVariables(params),
            }),
            // Refreshes the graph project list, and the customer's projectCount stat.
            invalidatesTags: ['project', 'customer'],
        }),

        customerProjectDefinitions: builder.query<ProjectDefinitionListRow[], CustomerProjectDefinitionsParams>({
            query: params => ({
                body: customerProjectDefinitionsQuery,
                variables: params,
            }),
            transformResponse: (response: CustomerProjectDefinitionsResult) =>
                (response.customer?.basicProjectList ?? []).filter((row): row is ProjectDefinitionListRow => Boolean(row)),
            providesTags: ['project'],
        }),

        setProjectPortfolioInclusion: builder.mutation<SetProjectPortfolioInclusionResult, SetProjectPortfolioInclusionParams>({
            query: params => ({
                body: setProjectPortfolioInclusionMutation,
                variables: params,
            }),
            // Re-reads includeInPortfolio from the server rather than trusting an optimistic local
            // flip - the toggle only ever reflects last-known server state (see PortfolioToggleCell).
            // 'customer' too: hasPortfolio (customersGraphSlice's `customers` query, which drives
            // the GNP nav section) is derived from this same flag and must update live, not just
            // on a hard refresh that happens to refetch everything.
            invalidatesTags: ['project', 'customer'],
        }),

        updateProject: builder.mutation<UpdateProjectResult, UpdateProjectParams>({
            query: params => ({
                body: updateProjectMutation,
                variables: { projectId: params.projectId, projectName: params.projectName.trim() },
            }),
            // The projects table reads the name from the graph list.
            invalidatesTags: ['project'],
        }),

        deleteProject: builder.mutation<DeleteProjectResult, DeleteProjectParams>({
            query: params => ({
                body: deleteProjectMutation,
                variables: params,
            }),
            // As with deleteCustomer, the caller invalidates once the receipt has been read rather
            // than the moment the cascade returns. See DeleteProjectDialog.
        }),

        projectStats: builder.query<ProjectStats | null, ProjectStatsParams>({
            query: params => ({
                body: projectStatsQuery,
                variables: params,
            }),
            transformResponse: (response: ProjectStatsResult) => response.project?.stats ?? null,
            providesTags: ['project'],
        }),

        customerStats: builder.query<CustomerStats | null, CustomerStatsParams>({
            query: params => ({
                body: customerStatsQuery,
                variables: params,
            }),
            transformResponse: (response: CustomerStatsResult) => response.customer?.stats ?? null,
            // Renaming an organisation, or adding and removing its projects, all move these figures.
            providesTags: ['customer'],
        }),
    }),
});

export const {
    useCreateCustomerMutation,
    useUpdateCustomerMutation,
    useDeleteCustomerMutation,
    useCustomerProjectDefinitionsQuery,
    useCreateProjectMutation,
    useSetProjectPortfolioInclusionMutation,
    useUpdateProjectMutation,
    useDeleteProjectMutation,
    useProjectStatsQuery,
    useCustomerStatsQuery,
} = adminGraphSlice;
